/**
 * Resolves with the visible element matching `selector`, or null after a
 * timeout. Used by the spotlight tour to wait for a route's anchor to mount
 * (event surfaces are lazy chunks that appear after Suspense resolves) before
 * highlighting it — and to gracefully skip a step whose anchor never appears.
 */
export function waitForAnchor(
  selector: string,
  opts: { timeoutMs?: number } = {},
): Promise<HTMLElement | null> {
  const timeoutMs = opts.timeoutMs ?? 8000;

  const visible = (el: Element | null): el is HTMLElement =>
    el instanceof HTMLElement &&
    (el.offsetParent !== null || el.getBoundingClientRect().width > 0);

  // Anchors may exist as hidden desktop/mobile breakpoint duplicates — take
  // the first VISIBLE match, not just the first match.
  const firstVisible = (): HTMLElement | null => {
    for (const el of document.querySelectorAll(selector)) {
      if (visible(el)) return el;
    }
    return null;
  };

  return new Promise((resolve) => {
    const existing = firstVisible();
    if (existing) {
      resolve(existing);
      return;
    }

    let settled = false;
    const settle = (el: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(el);
    };

    const observer = new MutationObserver(() => {
      const el = firstVisible();
      if (el) {
        // one frame so layout settles after the route/Suspense swap
        requestAnimationFrame(() => settle(el));
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => settle(null), timeoutMs);
  });
}
